import React, { useState, useEffect, useRef } from 'react';
import { ArrowRight, Users, Car, Briefcase, CheckCircle } from 'lucide-react';

const ServicesSection = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [hoveredCard, setHoveredCard] = useState(null);
  const sectionRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.2 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  const services = [
    {
      id: 'particulier',
      title: 'Propriétaire de voiture',
      subtitle: 'Particuliers',
      description: 'Réservez votre vidange à domicile en quelques clics. Service rapide, professionnel et sans contrainte.',
      image: '/images/particulier.png',
      ctaText: 'Réserver Atelier Mobile',
      ctaLink: 'https://reservation.faucon-bleu.ma/reservation',
      icon: Car,
      features: [
        'Service à domicile (Vidange / Pneumatique)',
        'Réservation en ligne simple',
        'Techniciens certifiés',
        'Huiles Shell premium'
      ],
      gradient: 'from-blue-500 to-indigo-600',
      bgGradient: 'from-blue-50 to-indigo-50'
    },
    {
      id: 'professionnel',
      title: 'Gestionnaire de flotte',
      subtitle: 'Professionnels',
      description: 'Optimisez l\'entretien de votre parc automobile avec un interlocuteur dédié et des solutions sur mesure.',
      image: '/images/professionels.png',
      ctaText: 'Services professionnels',
      ctaLink: 'https://business.faucon-bleu.ma',
      icon: Briefcase,
      features: [
        'Application mobile dédié',
        'Facturation simplifiée',
        'Suivi de flotte',
        'Tarifs préférentiels'
      ],
      gradient: 'from-green-500 to-emerald-600',
      bgGradient: 'from-green-50 to-emerald-50'
    }
  ];

  return (
    <section 
      ref={sectionRef}
      className="py-20 bg-white relative overflow-hidden"
      itemScope
      itemType="https://schema.org/Service"
    >
      {/* Background Elements */}
      <div className="absolute inset-0">
        <div className="absolute top-20 left-10 w-32 h-32 bg-blue-100 rounded-full opacity-50 animate-pulse" />
        <div className="absolute bottom-20 right-10 w-24 h-24 bg-green-100 rounded-full opacity-50 animate-pulse" style={{ animationDelay: '1s' }} />
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-40 h-40 bg-indigo-50 rounded-full opacity-30" />
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        
        {/* Section Header */}
        <div className={`text-center mb-16 transition-all duration-1000 ${
          isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
        }`}>
          <div className="inline-flex items-center px-4 py-2 bg-blue-100 text-blue-800 rounded-full text-sm font-medium mb-4">
            <Users className="w-4 h-4 mr-2" />
            Nos services adaptés à vos besoins
          </div>
          
          <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-6">
            Qui que vous soyez, nous avons{' '}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-indigo-600">
              la solution
            </span>
          </h2>
          
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Des services automobiles pensés pour répondre aux besoins spécifiques 
            des particuliers et des professionnels.
          </p>
        </div>

        {/* Services Grid */}
        <div className="grid lg:grid-cols-2 gap-8 lg:gap-12">
          {services.map((service, index) => {
            const Icon = service.icon;
            return (
              <div
                key={service.id}
                className={`group relative transition-all duration-700 ${
                  isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'
                }`}
                style={{ transitionDelay: `${index * 200}ms` }}
                onMouseEnter={() => setHoveredCard(service.id)}
                onMouseLeave={() => setHoveredCard(null)}
                itemScope
                itemType="https://schema.org/Service"
              >
                {/* Main Service Card */}
                <div className={`
                  relative bg-white rounded-3xl p-8 shadow-lg border border-gray-100
                  transition-all duration-500 transform
                  ${hoveredCard === service.id ? 'scale-105 shadow-2xl' : 'hover:shadow-xl'}
                `}>
                  
                  {/* Background Gradient */}
                  <div className={`
                    absolute inset-0 bg-gradient-to-br ${service.bgGradient} 
                    rounded-3xl opacity-0 transition-opacity duration-500
                    ${hoveredCard === service.id ? 'opacity-100' : 'group-hover:opacity-50'}
                  `} />
                  
                  <div className="relative z-10">
                    
                    {/* Service Icon & Badge */}
                    <div className="flex items-center justify-between mb-6">
                      <div className={`
                        inline-flex items-center px-4 py-2 rounded-full text-sm font-medium
                        bg-gradient-to-r ${service.gradient} text-white
                      `}>
                        <Icon className="w-4 h-4 mr-2" />
                        {service.subtitle}
                      </div>
                      
                      {hoveredCard === service.id && (
                        <div className="animate-bounce">
                          <CheckCircle className="w-6 h-6 text-green-500" />
                        </div>
                      )}
                    </div>

                    {/* Service Image */}
                    <div className="relative mb-6 h-48 rounded-2xl overflow-hidden">
                      <img
                        src={service.image}
                        alt={`${service.title} - ${service.subtitle}`}
                        className={`
                          w-full h-full object-cover transition-transform duration-700
                          ${hoveredCard === service.id ? 'scale-110' : ''}
                        `}
                        loading="lazy"
                        itemProp="image"
                      />
                      
                      {/* Overlay */}
                      <div className={`
                        absolute inset-0 bg-gradient-to-t from-black/20 to-transparent
                        transition-opacity duration-300
                        ${hoveredCard === service.id ? 'opacity-100' : 'opacity-0'}
                      `} />
                    </div>

                    {/* Content */}
                    <div className="space-y-4">
                      <h3 
                        className="text-2xl lg:text-3xl font-bold text-gray-900"
                        itemProp="name"
                      >
                        {service.title}
                      </h3>

                      <p 
                        className="text-gray-600 leading-relaxed"
                        itemProp="description"
                      >
                        {service.description}
                      </p>

                      {/* Features List */}
                      <div className={`
                        space-y-2 transition-all duration-500
                        ${hoveredCard === service.id ? 'opacity-100 max-h-40' : 'opacity-0 max-h-0 overflow-hidden'}
                      `}>
                        {service.features.map((feature, idx) => (
                          <div 
                            key={idx}
                            className="flex items-center text-sm text-gray-700"
                            style={{ transitionDelay: `${idx * 100}ms` }}
                          >
                            <CheckCircle className="w-4 h-4 text-green-500 mr-3 flex-shrink-0" />
                            {feature}
                          </div>
                        ))}
                      </div>

                      {/* CTA Button */}
                      <div className="pt-6">
                        <a
                          href={service.ctaLink}
                          target="_blank"
                          rel="noopener noreferrer"
                          className={`
                            group/btn inline-flex items-center justify-center w-full px-6 py-4
                            bg-gradient-to-r ${service.gradient} text-white font-semibold rounded-xl
                            transition-all duration-300 transform hover:scale-105 hover:shadow-lg
                          `}
                          itemProp="url"
                        >
                          <span>{service.ctaText}</span>
                          <ArrowRight className="ml-2 w-5 h-5 transition-transform group-hover/btn:translate-x-1" />
                        </a>
                      </div>
                    </div>
                  </div>

       
                </div>

                {/* Floating Stats */}
                {service.id === 'particulier' && (
                  <div className={`
                    absolute -top-[100px] -left-4 bg-white rounded-xl p-3 shadow-lg border
                    transition-all duration-700 ${hoveredCard === service.id ? 'translate-y-0 opacity-100' : 'translate-y-2 opacity-0'}
                  `}>
                    <div className="text-lg font-bold text-blue-600">500+</div>
                    <div className="text-xs text-gray-600">Clients satisfaits</div>
                  </div>
                )}

                {service.id === 'professionnel' && (
                  <div className={`
                    absolute -bottom-4 -right-4 bg-white rounded-xl p-3 shadow-lg border
                    transition-all duration-700 ${hoveredCard === service.id ? 'translate-y-0 opacity-100' : 'translate-y-2 opacity-0'}
                  `}>
                    <div className="text-lg font-bold text-green-600">50+</div>
                    <div className="text-xs text-gray-600">Entreprises partenaires</div>
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Bottom CTA */}
        <div className={`
          text-center mt-16 transition-all duration-1000 delay-700 ${
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }
        `}>
          <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-2xl p-8 border border-blue-100">
            <h3 className="text-2xl font-bold text-gray-900 mb-4">
              Pas sûr de quel service choisir ?
            </h3>
            <p className="text-gray-600 mb-6 max-w-2xl mx-auto">
              Contactez-nous directement et notre équipe vous orientera vers la solution 
              la plus adaptée à vos besoins.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="tel:+212702094593"
                className="inline-flex items-center justify-center px-6 py-3 bg-blue-600 text-white font-semibold rounded-lg hover:bg-blue-700 transition-colors"
              >
                📞 07 02 09 45 93
              </a>
              <a
                href="https://wa.me/212702094593"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center px-6 py-3 bg-green-600 text-white font-semibold rounded-lg hover:bg-green-700 transition-colors"
              >
                💬 WhatsApp
              </a>
            </div>
          </div>
        </div>
      </div>

      {/* Structured Data */}
      <script 
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "ItemList",
            "name": "Services Faucon Bleu",
            "description": "Services automobiles pour particuliers et professionnels",
            "itemListElement": services.map((service, index) => ({
              "@type": "Service",
              "position": index + 1,
              "name": service.title,
              "description": service.description,
              "provider": {
                "@type": "AutoRepair",
                "name": "Faucon Bleu"
              },
              "areaServed": {
                "@type": "City",
                "name": "Casablanca"
              },
              "url": service.ctaLink
            }))
          })
        }}
      />
    </section>
    
  );
};

export default ServicesSection;